import React, { Component } from 'react'
import { View, Platform } from 'react-native'
import styled from 'styled-components/native'

import Icon from '../../Atoms/Icon'
import { Text } from '../../Atoms/Typography'
import PageHeader from '../../Molecules/PageHeader'
import OpexCostColumn from './OpexCostColumn'
import ImpactWidget, { IMPACTS } from './ImpactWidget'
import { fteCostPerDivision, getImpacts } from './calculations'
import { thousandSeparator, percentage } from '../../../lib/formatters'
import ValueChangeText from '../../Molecules/ValueChangeText'
import theme from '../../../styles/theme'
import { translate } from '../../../services/translate'

const isIOS = Platform.OS === 'ios'

const DIVISION_WIDTH = 75
const DIVISION_HEIGHT = 3 / 4 * 100

const Container = styled(View)`
  flex: 1;
  flex-direction: column;
  background-color: ${theme.colours.white};
`

const Content = styled(View)`
  height: 100%;
`

const Row = styled(View)`
  flex-direction: row;
`

const DivisionContent = styled(View)`
  flex-direction: row;
  height: 100%;
`

const DivisionSection = styled(View)`
  width: ${DIVISION_WIDTH}%;
`

const ImpactSection = styled(View)`
  width: ${100 - DIVISION_WIDTH}%;
`

const ImpactContent = styled(View)`
  flex: 1;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
`

const PeopleAndCostImpactSection = styled(View)`
  width: ${DIVISION_WIDTH}%;
`

const PeopleAndCostImpactDivisionContent = styled(View)`
  flex-direction: row;
  flex: 1;
`

const PeopleAndCostImpactDivision = styled(View)`
  padding: ${theme.gutterUnit}px;
  padding-bottom: ${3 * theme.gutterUnit}px;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.selected ? theme.colours.active : theme.colours.white};
  border-color: ${theme.colours.grey};
  border-left-width: ${props => props.showBorder ? 1 : 0}px;
  border-right-width: ${props => props.showBorder ? 1 : 0}px;
`

const TotalSection = styled(View)`
  width: ${100 - DIVISION_WIDTH}%;
`

const TotalContent = styled(View)`
  flex: 1;
  padding: ${theme.gutterUnit}px;
  padding-bottom: ${3 * theme.gutterUnit}px;
  justify-content: space-between;
  align-items: center;
`

const TotalRow = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-left: ${2 * theme.gutterUnit}px;
  padding-right: ${3 * theme.gutterUnit}px;
`

const formatValue = (val, addPlusSign, unit) => {
  if (parseInt(val) === 0) {
    return '-'
  }
  if (val < 0) {
    return `(${thousandSeparator(val * -1)}${unit || ''})`
  }
  return `${addPlusSign ? '+' : ''}${thousandSeparator(val)}${unit || ''}`
}

const getImpactFromField = (division, field, negativeIsGood = false) => {
  const val = division[field]
  if (val === 0) {
    return IMPACTS.NONE
  }

  if ((negativeIsGood && val < 0) || (!negativeIsGood && val > 0)) {
    return IMPACTS.GOOD
  }

  return IMPACTS.BAD
}

const impacts = [
  { heading: 'Customer\nSatisfaction', icon: 'nps', field: 'nps', impactField: 'customerSatisfaction' },
  { heading: 'Customer\nComplaints', icon: 'dislike', field: 'complaints', isCost: true, impactField: 'complaints', negativeIsGood: true },
  { heading: 'Employee\nEngagement', icon: 'smile', field: 'employeeEngagement', type: 'percentage', changeUnit: '%', impactField: 'employeeEngagement' },
  { heading: 'Risk\nIncidents', icon: 'danger', field: 'riskIncidents', isCost: true, impactField: 'riskIncidents', negativeIsGood: true },
  { heading: 'Community\nTrust', icon: 'leaf', field: 'communityTrust', type: 'percentage', changeUnit: '%', impactField: 'riskIncidents', negativeIsGood: true },
  { heading: 'Regulator\nSanctions', icon: 'gavel', field: 'regulatorActions', isCost: true, impactField: 'riskIncidents', negativeIsGood: true }
]

export default class Opex extends Component {
  shouldComponentUpdate (nextProps, nextState) {
    if (nextProps.selectedDivision !== this.props.selectedDivision) {
      return true
    }
    if (nextProps.decisions !== this.props.decisions) {
      return true
    }
    if (nextProps.divisions !== this.props.divisions) {
      return true
    }
    if (nextProps.enableChanges !== this.props.enableChanges) {
      return true
    }
    return false
  }

  render () {
    const { summaries, enableChanges, opex, decisions, onChangeValue, selectedDivision, onDivisionSelect } = this.props
    const { divisions } = opex
    const peopleAndCostImpactPerDivision = fteCostPerDivision(opex, decisions)
    const totalFTEChange = peopleAndCostImpactPerDivision.reduce((res, d) => res + d.fteChange, 0)
    const totalChangeInCost = peopleAndCostImpactPerDivision.reduce((res, d) => res + d.changeInCost, 0)

    const impactValues = getImpacts(opex, decisions)

    const selectedDivisionDetails = divisions.find(d => d.type === selectedDivision)

    return (
      <Container>

        <Content>
          <Row style={{ height: `${DIVISION_HEIGHT}%` }}>
            <DivisionSection>
              <PageHeader text={translate('operatingCosts.screenHeader', 'OPERATING COSTS - PEOPLE').toUpperCase()} />
              <DivisionContent>
                {divisions.map((d, index) => (
                  <OpexCostColumn
                    index={index}
                    selected={d.type === selectedDivision}
                    onPress={() => onDivisionSelect(d.type)}
                    key={d.id}
                    data={d}
                    numberOfColumns={divisions.length}
                    enableChanges={enableChanges}
                    decision={decisions[d.type]}
                    onChangeValue={onChangeValue}
                    effectiveDecision={impactValues.effectiveDecisions[d.type]}
                    type={d.type}
                  />
                ))}
              </DivisionContent>
            </DivisionSection>
            <ImpactSection>
              <PageHeader hideTeam text={translate('operatingCosts.impact', 'IMPACT').toUpperCase()} />
              <ImpactContent>
                {impacts.map((impact, i) => {
                  const calculatedImpact = impactValues[impact.field] || {}
                  const formattedValue = impact.type === 'percentage' ? percentage(calculatedImpact.value || 0, 0) : formatValue(calculatedImpact.value ? calculatedImpact.value.toFixed(0) : 0)
                  return (
                    <ImpactWidget
                      key={i}
                      index={i}
                      heading={impact.field === 'communityTrust' ? summaries.find(({ type }) => type === 'Community').heading.split(' ').join('\n') : impact.heading}
                      icon={impact.icon}
                      value={formattedValue}
                      change={calculatedImpact.change ? calculatedImpact.change.toFixed(0) : 0}
                      isCost={impact.isCost}
                      changeUnit={impact.changeUnit}
                      impact={getImpactFromField(selectedDivisionDetails, impact.impactField, impact.negativeIsGood)}
                    />
                  )
                })}
              </ImpactContent>
            </ImpactSection>
          </Row>
          <Row style={{ height: `${100 - DIVISION_HEIGHT}%` }}>
            <PeopleAndCostImpactSection>
              <PageHeader hideTeam text={translate('operatingCosts.screenSubHeader', 'PEOPLE & COST IMPACT').toUpperCase()} />
              <PeopleAndCostImpactDivisionContent>
                {divisions.map((d, index) => {
                  const { fteChange, changeInCost } = peopleAndCostImpactPerDivision[index]
                  return (
                    <PeopleAndCostImpactDivision key={d.id} style={{ width: `${100 / divisions.length}%` }} selected={d.type === selectedDivision} showBorder={d.type === 'SALARY_GROWTH'}>
                      <Text proximaBold size={isIOS ? 'l' : 'm'}>{d.name}</Text>
                      <Text medium size='l'>{formatValue(fteChange.toFixed(0), true, ' FTE')}</Text>
                      <ValueChangeText negativeInBrackets dashIfZero size='l' medium value={changeInCost.toFixed(0)} isCost changeUnit=' m' />
                    </PeopleAndCostImpactDivision>
                  )
                })}
              </PeopleAndCostImpactDivisionContent>
            </PeopleAndCostImpactSection>
            <TotalSection>
              <PageHeader hideTeam text='TOTAL' />
              <TotalContent>
                <Text proximaBold size='l' />
                <TotalRow>
                  <Icon size={isIOS ? 'xxxl' : 'xl'} colour={theme.colours.black} name='group' />
                  <View style={{ flex: 1 }}>
                    <Text center medium size='l'>{formatValue(totalFTEChange.toFixed(0), true, ' FTE')}</Text>
                  </View>
                </TotalRow>
                <TotalRow>
                  <Icon size={isIOS ? 'xxxl' : 'xl'} colour={theme.colours.black} name='moneybag' />
                  <View style={{ flex: 1 }}>
                    <ValueChangeText center negativeInBrackets dashIfZero size='l' medium value={totalChangeInCost.toFixed(0)} isCost changeUnit=' m' />
                  </View>
                </TotalRow>
              </TotalContent>
            </TotalSection>
          </Row>
        </Content>
      </Container>
    )
  }
}
